import React, { useCallback, useState } from 'react';
import type { ChangeCabDeck, ChangeCabSlide, ChangeCabSlideField, ChangeMeetingItem } from '../../../types/changes';
import {
  cabDeckThemeToCssVars,
  resolveCapaTitle,
  type CabDeckTheme,
} from '../cabDeckTheme';
import { formatCabSlideDate, labelForCabField, resolveCabSlideTemplate } from '../cabPresentationTemplates';
import '../cabPresentationTheme.css';
import { CabDeckSlideCanvas, meetingItemFields } from './CabDeckSlideCanvas';
import CabStageChrome from './CabStageChrome';
import PetronectLogo, { PetronectMark } from './PetronectLogo';
import { formatChangePublicId } from '../changePublicId';

export default function CabSlideStage({
  slide,
  current,
  remaining,
  countdown,
  elapsed,
  totalElapsed,
  slideLabel,
  fieldCatalog,
  meetingDate,
  theme,
  deck,
  deckSlideIndex = 0,
  fontScale = 1,
}: {
  slide: ChangeCabSlide | null;
  current: ChangeMeetingItem;
  remaining: number;
  countdown: number;
  elapsed: number;
  totalElapsed: number;
  slideLabel?: string;
  fieldCatalog: ChangeCabSlideField[];
  meetingDate?: string | null;
  theme: CabDeckTheme;
  deck?: ChangeCabDeck | null;
  deckSlideIndex?: number;
  /** Escala de leitura na apresentação (1 = 100%). */
  fontScale?: number;
}) {
  const changeHeading = `#${current.change_id} ${current.title}`;
  const scale = Number.isFinite(fontScale) && fontScale > 0 ? fontScale : 1;
  const themeStyle = {
    ...cabDeckThemeToCssVars(theme),
    ['--cab-font-scale' as string]: String(scale),
  };
  const [activityNonce, setActivityNonce] = useState(0);
  const lastBumpRef = React.useRef(0);
  const bumpActivity = useCallback(() => {
    const now = Date.now();
    if (now - lastBumpRef.current < 350) return;
    lastBumpRef.current = now;
    setActivityNonce((n) => n + 1);
  }, []);

  const chrome = (
    <CabStageChrome
      remaining={remaining}
      countdown={countdown}
      elapsed={elapsed}
      totalElapsed={totalElapsed}
      slideLabel={slideLabel}
      activityNonce={activityNonce}
    />
  );

  const wrap = (inner: React.ReactNode, extra?: React.CSSProperties) => (
    <div
      className="position-relative cab-stage-shell"
      style={{ aspectRatio: '16 / 9', ...extra }}
      data-font-scale={fontScale}
      onMouseMove={bumpActivity}
      onMouseEnter={bumpActivity}
    >
      {inner}
      {chrome}
    </div>
  );

  const deckSlide = deck?.slides?.[deckSlideIndex] || null;
  if (deck && deckSlide) {
    const deckTheme = { ...theme, ...(deck.theme || {}) };
    return wrap(
      <CabDeckSlideCanvas
        slide={deckSlide}
        theme={{ ...deckTheme }}
        fontScale={fontScale}
        fields={{
          ...meetingItemFields(current),
          title: resolveCapaTitle(theme, changeHeading),
        }}
      />,
    );
  }

  const template = resolveCabSlideTemplate(slide?.title);
  const railTitle = (slide?.title || 'MUDANÇA').toUpperCase();
  const rail = (
    <aside className="cab-cco-rail" aria-label="Identidade do slide">
      <div className="cab-cco-rail-body">
        <div className="cab-cco-rail-accent" />
        <div className="cab-cco-rail-title">{railTitle}</div>
        <div className="cab-cco-rail-sub">{changeHeading}</div>
      </div>
      <div className="cab-cco-rail-logo" aria-hidden="true">
        <PetronectMark variant="white" />
      </div>
    </aside>
  );

  if (!slide) {
    return wrap(
      <div className="cab-cco-stage" data-template="fields" style={themeStyle}>
        {rail}
        <div className="cab-cco-main">
          <p className="mb-0" style={{ color: 'var(--cab-cco-blue)' }}>
            Cadastre o modelo em Cadastros → Apresentação CAB ou edite a apresentação nesta mudança.
          </p>
        </div>
      </div>,
    );
  }

  if (template === 'capa') {
    return wrap(
      <div className="cab-cco-stage" data-template="capa" style={themeStyle}>
        <div className="cab-cco-capa-brand">
          <PetronectLogo />
        </div>
        <div className="cab-cco-capa-title">{resolveCapaTitle(theme, changeHeading)}</div>
        <div className="cab-cco-capa-date">{formatCabSlideDate(meetingDate)}</div>
        <div className="cab-cco-capa-mark">
          <PetronectMark variant="green" />
        </div>
      </div>,
    );
  }

  if (template === 'telas') {
    return wrap(
      <div className="cab-cco-stage" data-template="telas" style={themeStyle}>
        {rail}
        <div className="cab-cco-main">
          <div className="cab-cco-telas-frame">
            Área de print / evidência visual (anexo da mudança — em breve)
          </div>
        </div>
      </div>,
    );
  }

  if (template === 'riscos_impactos') {
    return wrap(
      <div className="cab-cco-stage" data-template="riscos_impactos" style={themeStyle}>
        {rail}
        <div className="cab-cco-main">
          <div className="cab-cco-section">
            <div className="cab-cco-section-head">{railTitle}</div>
          </div>
          <table className="cab-cco-riscos-table">
            <thead>
              <tr>
                <th>Campo</th>
                <th>Valor</th>
              </tr>
            </thead>
            <tbody>
              {slide.field_keys.map((key) => (
                <tr key={key}>
                  <td>{labelForCabField(key, fieldCatalog)}</td>
                  <td>{current.fields?.[key] || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>,
    );
  }

  const metaKeys = new Set(['go_live', 'requester_name', 'owner_name', 'priority']);
  const bodyKeys = slide.field_keys.filter((key) => !metaKeys.has(key));
  const metaEntries = slide.field_keys
    .filter((key) => metaKeys.has(key))
    .map((key) => ({
      key,
      label: labelForCabField(key, fieldCatalog),
      value: current.fields?.[key] || '—',
    }));

  return wrap(
    <div className="cab-cco-stage" data-template="fields" style={themeStyle}>
      {rail}
      <div className="cab-cco-main">
        {(bodyKeys.length ? bodyKeys : slide.field_keys).map((key) => (
          <div className="cab-cco-section" key={key}>
            <div className="cab-cco-section-head">{labelForCabField(key, fieldCatalog)}</div>
            <div className="cab-cco-section-body">{current.fields?.[key] || '—'}</div>
          </div>
        ))}
        {metaEntries.length ? (
          <div className="cab-cco-meta-bar">
            {metaEntries.map((item) => (
              <span key={item.key}>
                {item.label}: {item.value}
              </span>
            ))}
          </div>
        ) : null}
      </div>
    </div>,
  );
}
